import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import InputLabel from "@mui/material/InputLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import { visuallyHidden } from "@mui/utils";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { isValidEmail } from "../../utils/validation";

const StyledBox = styled("div")(({ theme }) => ({
  alignSelf: "center",
  width: "100%",
  maxWidth: "90vw", // Limite la largeur à 90% de la largeur de la fenêtre
  height: "50vw", // Hauteur basée sur la largeur de l'écran
  margin: "0 auto", // Centre horizontalement
  marginTop: theme.spacing(8),
  borderRadius: (theme.vars || theme).shape.borderRadius,
  outline: "6px solid",
  outlineColor: "hsla(220, 25%, 80%, 0.2)",
  border: "1px solid",
  borderColor: (theme.vars || theme).palette.grey[200],
  boxShadow: "0 0 12px 8px hsla(220, 25%, 80%, 0.2)", // Ajuste l'ombre
  backgroundImage: `url('/images/templates/Armon Alef.png')`,
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  [theme.breakpoints.up("sm")]: {
    height: "40vw", // Hauteur ajustée pour les écrans plus grands
    maxWidth: "70vw", // Réduit la largeur pour les écrans plus grands
  },
  [theme.breakpoints.up("md")]: {
    marginTop: theme.spacing(10),
    height: "30vw",
    maxWidth: "60vw",
  },
  [theme.breakpoints.up("lg")]: {
    height: "25vw",
    maxWidth: "50vw",
  },
  ...theme.applyStyles("dark", {
    boxShadow: "0 0 24px 12px hsla(210, 100%, 25%, 0.2)",
    backgroundImage: `url(${process.env.TEMPLATE_IMAGE_URL || "https://mui.com"}/static/screenshots/material-ui/getting-started/templates/dashboard-dark.jpg)`,
    outlineColor: "hsla(220, 20%, 42%, 0.1)",
    borderColor: (theme.vars || theme).palette.grey[700],
  }),
}));

export default function Hero() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [emailError, setEmailError] = useState("");

  const handleSubmit = () => {
    setEmailError("");

    if (!email) {
      setMessage(t("hero.errors.emailRequired"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setEmailError(t("hero.errors.emailRequired"));
      return;
    }

    if (!isValidEmail(email)) {
      setMessage(t("hero.errors.emailInvalid"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setEmailError(t("hero.errors.emailInvalid"));
      return;
    }

    fetch(`${process.env.REACT_APP_BASE_URL}/infos/subscribe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error("Failed to subscribe");
        }
      })
      .then((data) => {
        setMessage(t("hero.success.subscription"));
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setEmail("");
        setEmailError("");
      })
      .catch((error) => {
        console.error("Erreur lors de l'inscription :", error);
        setMessage(t("hero.errors.subscriptionFailed"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  return (
    <Box
      id="hero"
      sx={(theme) => ({
        width: "100%",
        backgroundRepeat: "no-repeat",
        backgroundImage:
          "radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 90%), transparent)",
        ...theme.applyStyles("dark", {
          backgroundImage:
            "radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 16%), transparent)",
        }),
      })}
    >
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 14, sm: 20 },
          pb: { xs: 8, sm: 12 },
        }}
      >
        <Stack
          spacing={2}
          useFlexGap
          sx={{ alignItems: "center", width: { xs: "100%", sm: "70%" } }}
        >
          <Typography
            variant="h1"
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              textAlign: { xs: "center", sm: "left" }, // Centré en mode mobile
              fontSize: "clamp(3rem, 10vw, 3.5rem)",
            }}
          >
            {t("hero.title.part1")}&nbsp;
            <Typography
              component="span"
              variant="h1"
              sx={(theme) => ({
                fontSize: "inherit",
                color: "primary.main",
                ...theme.applyStyles("dark", {
                  color: "primary.light",
                }),
              })}
            >
              {t("hero.title.part2")}
            </Typography>
          </Typography>
          <Typography
            sx={{
              textAlign: "center",
              color: "text.secondary",
              width: { sm: "100%", md: "80%" },
            }}
          >
            {t("hero.description")}
          </Typography>
        </Stack>
        <StyledBox id="image" />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}